import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  BOSS_DASHBOARD_EVENT_TYPES,
  bossDashboardPageNumber,
  bossDashboardPageTitle,
} from "../../config/bossDashboardPages";
import { fetchBossDashboardActivities, bossActivityTimestampMillis } from "../../services/bossDashboardActivityService";
import { downloadCsv } from "../../utils/csvExport";

function fmtTime(v) {
  if (!v) return "—";
  const ms = bossActivityTimestampMillis(v);
  if (!ms) return "—";
  return new Date(ms).toLocaleString("en-IN");
}

function asText(v) {
  if (v == null || v === "") return "—";
  return String(v);
}

function formatDuration(sec) {
  const n = Number(sec);
  if (!Number.isFinite(n) || n < 0) return "—";
  if (n < 60) return `${n}s`;
  const m = Math.floor(n / 60);
  const s = n % 60;
  return s ? `${m}m ${s}s` : `${m}m`;
}

function eventBadgeClass(eventType) {
  const t = String(eventType || "");
  if (t === "session_start") return "bg-emerald-100 text-emerald-900";
  if (t === "session_end") return "bg-slate-200 text-slate-800";
  if (t === "page_view") return "bg-indigo-100 text-indigo-900";
  if (t === "action") return "bg-amber-100 text-amber-950";
  if (t === "app_background" || t === "app_resumed") return "bg-violet-100 text-violet-900";
  return "bg-slate-100 text-slate-700";
}

function actionLabel(row) {
  const meta = row.metadata && typeof row.metadata === "object" ? row.metadata : {};
  if (row.eventType === "action") {
    const parts = [];
    if (meta.action) parts.push(String(meta.action));
    if (meta.actionId) parts.push(String(meta.actionId));
    if (meta.from != null || meta.to != null) parts.push(`${meta.from ?? "—"} → ${meta.to ?? "—"}`);
    if (parts.length) return parts.join(" · ");
  }
  return asText(row.details);
}

function deviceSummary(device) {
  if (!device || typeof device !== "object") return "—";
  const d = device;
  const platform = String(d.platform || "").toLowerCase();
  if (platform === "android") {
    return [d.brand, d.model].filter(Boolean).join(" ") || d.device || "Android";
  }
  if (platform === "ios") {
    return [d.name, d.model].filter(Boolean).join(" ") || "iOS";
  }
  if (platform === "web") {
    return d.browserName || "Web";
  }
  return d.platform || d.model || "—";
}

function serializeRowForCsv(r) {
  const meta = r.metadata && typeof r.metadata === "object" ? r.metadata : {};
  return {
    timestamp: fmtTime(r.timestamp),
    eventType: r.eventType || "",
    username: r.username || "",
    userId: r.userId || "",
    sessionId: r.sessionId || "",
    pageTitle: bossDashboardPageTitle(r.pageId, r.pageTitle),
    pageId: r.pageId || "",
    pageNumber: bossDashboardPageNumber(r.pageId, r.pageNumber) ?? "",
    navigation: r.navigation || "",
    branch: r.branch || "",
    details: r.details || "",
    durationSeconds: r.durationSeconds ?? "",
    action: meta.action || "",
    actionId: meta.actionId || "",
    appName: r.app?.name || "",
    appVersion: r.app?.version || "",
    device: deviceSummary(r.device),
  };
}

export default function BossDashboardActivityTab() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [eventFilter, setEventFilter] = useState("all");
  const [branchFilter, setBranchFilter] = useState("all");
  const [sessionFilter, setSessionFilter] = useState("");
  const [viewMode, setViewMode] = useState("list");
  const [selectedRow, setSelectedRow] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await fetchBossDashboardActivities(500);
      setRows(data);
    } catch (e) {
      setError(e?.message || "Failed to load boss dashboard activities.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const branchOptions = useMemo(
    () => [...new Set(rows.map((r) => String(r.branch || "").trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b)),
    [rows]
  );

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const sessionQ = sessionFilter.trim().toLowerCase();
    return rows.filter((r) => {
      if (eventFilter !== "all" && String(r.eventType || "") !== eventFilter) return false;
      if (branchFilter !== "all" && String(r.branch || "").trim() !== branchFilter) return false;
      if (sessionQ && !String(r.sessionId || "").toLowerCase().includes(sessionQ)) return false;
      if (!q) return true;
      const meta = r.metadata && typeof r.metadata === "object" ? r.metadata : {};
      const hay = [
        r.eventType,
        r.username,
        r.userId,
        r.sessionId,
        r.pageId,
        r.pageTitle,
        r.navigation,
        r.branch,
        r.details,
        meta.action,
        meta.actionId,
        meta.from,
        meta.to,
        r.app?.name,
        r.app?.package,
        deviceSummary(r.device),
      ]
        .map((x) => String(x || "").toLowerCase())
        .join(" ");
      return hay.includes(q);
    });
  }, [rows, search, eventFilter, branchFilter, sessionFilter]);

  const sessionsGrouped = useMemo(() => {
    const map = new Map();
    for (const r of filteredRows) {
      const sid = String(r.sessionId || "").trim() || "(no session)";
      if (!map.has(sid)) {
        map.set(sid, {
          sessionId: sid,
          username: r.username,
          branch: r.branch,
          events: [],
          firstMs: Infinity,
          lastMs: 0,
        });
      }
      const g = map.get(sid);
      g.events.push(r);
      const ms = bossActivityTimestampMillis(r.timestamp);
      if (ms) {
        g.firstMs = Math.min(g.firstMs, ms);
        g.lastMs = Math.max(g.lastMs, ms);
      }
    }
    const list = Array.from(map.values());
    for (const g of list) {
      g.events.sort((a, b) => bossActivityTimestampMillis(a.timestamp) - bossActivityTimestampMillis(b.timestamp));
    }
    list.sort((a, b) => (b.lastMs || 0) - (a.lastMs || 0));
    return list;
  }, [filteredRows]);

  const summary = useMemo(() => {
    const uniqueUsers = new Set(filteredRows.map((r) => String(r.username || "").trim()).filter(Boolean)).size;
    const uniqueSessions = new Set(filteredRows.map((r) => String(r.sessionId || "").trim()).filter(Boolean)).size;
    const pageViews = filteredRows.filter((r) => r.eventType === "page_view").length;
    const actions = filteredRows.filter((r) => r.eventType === "action").length;
    return {
      total: filteredRows.length,
      uniqueUsers,
      uniqueSessions,
      pageViews,
      actions,
    };
  }, [filteredRows]);

  const exportCsv = () => {
    downloadCsv(
      `boss-dashboard-activity-${new Date().toISOString().slice(0, 10)}.csv`,
      [
        { key: "timestamp", header: "timestamp" },
        { key: "eventType", header: "eventType" },
        { key: "username", header: "username" },
        { key: "userId", header: "userId" },
        { key: "sessionId", header: "sessionId" },
        { key: "pageTitle", header: "pageTitle" },
        { key: "pageId", header: "pageId" },
        { key: "pageNumber", header: "pageNumber" },
        { key: "navigation", header: "navigation" },
        { key: "branch", header: "branch" },
        { key: "details", header: "details" },
        { key: "durationSeconds", header: "durationSeconds" },
        { key: "action", header: "action" },
        { key: "actionId", header: "actionId" },
        { key: "appName", header: "appName" },
        { key: "appVersion", header: "appVersion" },
        { key: "device", header: "device" },
      ],
      filteredRows.map(serializeRowForCsv)
    );
  };

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Boss dashboard activity</h2>
          <p className="text-sm text-slate-600">
            Mobile boss-dashboard telemetry from{" "}
            <code className="rounded bg-slate-100 px-1">boss_dashboard_activities</code> (sessions, page views,
            actions, lifecycle).
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={exportCsv}
            disabled={!filteredRows.length}
            className="rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-800 hover:bg-slate-50 disabled:opacity-40"
          >
            Export CSV
          </button>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-50"
          >
            {loading ? "Loading…" : "Refresh"}
          </button>
        </div>
      </div>

      <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
          <p className="text-[11px] uppercase tracking-wide text-slate-500">Events</p>
          <p className="text-lg font-semibold text-slate-900">{summary.total}</p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
          <p className="text-[11px] uppercase tracking-wide text-slate-500">Users</p>
          <p className="text-lg font-semibold text-slate-900">{summary.uniqueUsers}</p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
          <p className="text-[11px] uppercase tracking-wide text-slate-500">Sessions</p>
          <p className="text-lg font-semibold text-slate-900">{summary.uniqueSessions}</p>
        </div>
        <div className="rounded-xl border border-indigo-100 bg-indigo-50/50 p-3">
          <p className="text-[11px] uppercase tracking-wide text-indigo-700">Page views</p>
          <p className="text-lg font-semibold text-indigo-950">{summary.pageViews}</p>
        </div>
        <div className="rounded-xl border border-amber-100 bg-amber-50/50 p-3">
          <p className="text-[11px] uppercase tracking-wide text-amber-800">Actions</p>
          <p className="text-lg font-semibold text-amber-950">{summary.actions}</p>
        </div>
      </div>

      <div className="mb-4 grid gap-2 md:grid-cols-2 lg:grid-cols-5">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search user, page, session, action…"
          className="rounded-lg border border-slate-300 px-3 py-2 text-sm lg:col-span-2"
        />
        <select
          value={eventFilter}
          onChange={(e) => setEventFilter(e.target.value)}
          className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
        >
          <option value="all">All event types</option>
          {BOSS_DASHBOARD_EVENT_TYPES.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
        <select
          value={branchFilter}
          onChange={(e) => setBranchFilter(e.target.value)}
          className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
        >
          <option value="all">All branches</option>
          {branchOptions.map((b) => (
            <option key={b} value={b}>
              {b}
            </option>
          ))}
        </select>
        <input
          value={sessionFilter}
          onChange={(e) => setSessionFilter(e.target.value)}
          placeholder="Session ID contains…"
          className="rounded-lg border border-slate-300 px-3 py-2 font-mono text-xs"
        />
      </div>

      <div className="mb-3 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => setViewMode("list")}
          className={`rounded-lg px-3 py-1.5 text-xs font-semibold ${
            viewMode === "list" ? "bg-indigo-600 text-white" : "border border-slate-300 bg-white text-slate-700"
          }`}
        >
          Event list
        </button>
        <button
          type="button"
          onClick={() => setViewMode("sessions")}
          className={`rounded-lg px-3 py-1.5 text-xs font-semibold ${
            viewMode === "sessions" ? "bg-indigo-600 text-white" : "border border-slate-300 bg-white text-slate-700"
          }`}
        >
          By session
        </button>
      </div>

      {error ? (
        <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
      ) : null}

      {viewMode === "sessions" ? (
        <div className="space-y-3 max-h-[560px] overflow-y-auto">
          {loading ? (
            <p className="text-sm text-slate-500">Loading…</p>
          ) : sessionsGrouped.length === 0 ? (
            <p className="text-sm text-slate-500">No sessions match filters.</p>
          ) : (
            sessionsGrouped.map((g) => (
              <div key={g.sessionId} className="rounded-xl border border-slate-200 bg-slate-50/50 p-3">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <div>
                    <p className="font-mono text-xs text-slate-600">{g.sessionId}</p>
                    <p className="text-sm font-semibold text-slate-900">
                      {asText(g.username)} · {g.events.length} events
                    </p>
                  </div>
                  <p className="text-xs text-slate-500">
                    {g.firstMs !== Infinity ? fmtTime(g.firstMs) : "—"} → {g.lastMs ? fmtTime(g.lastMs) : "—"}
                  </p>
                </div>
                <ol className="mt-2 space-y-1 border-l-2 border-indigo-200 pl-3">
                  {g.events.map((ev) => (
                    <li key={ev.id}>
                      <button
                        type="button"
                        onClick={() => setSelectedRow(ev)}
                        className="w-full rounded-lg px-2 py-1.5 text-left text-xs hover:bg-white"
                      >
                        <span className="text-slate-500">{fmtTime(ev.timestamp)}</span>{" "}
                        <span className={`ml-1 rounded px-1.5 py-0.5 font-bold uppercase ${eventBadgeClass(ev.eventType)}`}>
                          {ev.eventType}
                        </span>
                        {ev.pageId ? (
                          <span className="ml-1 text-slate-800">
                            {bossDashboardPageTitle(ev.pageId, ev.pageTitle)}
                            {ev.durationSeconds != null ? ` · ${formatDuration(ev.durationSeconds)}` : ""}
                          </span>
                        ) : (
                          <span className="ml-1 text-slate-700">{actionLabel(ev)}</span>
                        )}
                      </button>
                    </li>
                  ))}
                </ol>
              </div>
            ))
          )}
        </div>
      ) : (
        <div className="overflow-auto rounded-xl border border-slate-200">
          <table className="min-w-[1200px] w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-600">
              <tr>
                <th className="p-2 text-left">Time</th>
                <th className="p-2 text-left">Event</th>
                <th className="p-2 text-left">User</th>
                <th className="p-2 text-left">Page</th>
                <th className="p-2 text-left">Branch</th>
                <th className="p-2 text-left">Details</th>
                <th className="p-2 text-right">Duration</th>
                <th className="p-2 text-left">Device</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((r) => (
                <tr
                  key={r.id}
                  className="cursor-pointer border-t border-slate-100 align-top hover:bg-indigo-50/40"
                  onClick={() => setSelectedRow(r)}
                  title="Click for full document"
                >
                  <td className="whitespace-nowrap p-2 text-xs">{fmtTime(r.timestamp)}</td>
                  <td className="p-2">
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${eventBadgeClass(r.eventType)}`}>
                      {asText(r.eventType)}
                    </span>
                  </td>
                  <td className="p-2">
                    <div className="font-medium text-slate-900">{asText(r.username)}</div>
                    <div className="font-mono text-[10px] text-slate-500">{asText(r.userId)}</div>
                  </td>
                  <td className="p-2">
                    {r.pageId ? (
                      <>
                        <div>{bossDashboardPageTitle(r.pageId, r.pageTitle)}</div>
                        <div className="text-[10px] text-slate-500">
                          #{bossDashboardPageNumber(r.pageId, r.pageNumber) ?? "—"} · {asText(r.pageId)} ·{" "}
                          {asText(r.navigation)}
                        </div>
                      </>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="p-2">{asText(r.branch)}</td>
                  <td className="max-w-[14rem] p-2 text-xs text-slate-700">{actionLabel(r)}</td>
                  <td className="p-2 text-right tabular-nums">{formatDuration(r.durationSeconds)}</td>
                  <td className="p-2 text-xs text-slate-600">{deviceSummary(r.device)}</td>
                </tr>
              ))}
              {!loading && filteredRows.length === 0 ? (
                <tr>
                  <td colSpan={8} className="p-4 text-center text-sm text-slate-500">
                    No activity rows found.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      )}

      {selectedRow ? (
        <div className="fixed inset-0 z-[1400] flex items-center justify-center bg-black/45 p-4">
          <div className="w-full max-w-4xl rounded-2xl border border-slate-200 bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
              <div>
                <h3 className="text-base font-semibold text-slate-900">Activity detail</h3>
                <p className="text-xs text-slate-500">
                  {asText(selectedRow.eventType)} · {fmtTime(selectedRow.timestamp)}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setSelectedRow(null)}
                className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
              >
                Close
              </button>
            </div>
            <div className="max-h-[75vh] overflow-auto p-4">
              <div className="grid gap-3 md:grid-cols-2">
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm">
                  <p className="text-xs uppercase tracking-wide text-slate-500">Session & user</p>
                  <p>Session: {asText(selectedRow.sessionId)}</p>
                  <p>User: {asText(selectedRow.username)}</p>
                  <p>UID: {asText(selectedRow.userId)}</p>
                  <p>Branch: {asText(selectedRow.branch)}</p>
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm">
                  <p className="text-xs uppercase tracking-wide text-slate-500">Page</p>
                  <p>Title: {bossDashboardPageTitle(selectedRow.pageId, selectedRow.pageTitle)}</p>
                  <p>ID: {asText(selectedRow.pageId)}</p>
                  <p>Number: {bossDashboardPageNumber(selectedRow.pageId, selectedRow.pageNumber) ?? "—"}</p>
                  <p>Navigation: {asText(selectedRow.navigation)}</p>
                  <p>Details: {asText(selectedRow.details)}</p>
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm">
                  <p className="text-xs uppercase tracking-wide text-slate-500">Timing</p>
                  <p>Started: {fmtTime(selectedRow.startedAt)}</p>
                  <p>Ended: {fmtTime(selectedRow.endedAt)}</p>
                  <p>Duration: {formatDuration(selectedRow.durationSeconds)}</p>
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm">
                  <p className="text-xs uppercase tracking-wide text-slate-500">App</p>
                  <p>Name: {asText(selectedRow.app?.name)}</p>
                  <p>Package: {asText(selectedRow.app?.package)}</p>
                  <p>Version: {asText(selectedRow.app?.version)} · Build {asText(selectedRow.app?.buildNumber)}</p>
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm md:col-span-2">
                  <p className="text-xs uppercase tracking-wide text-slate-500">Device</p>
                  <pre className="mt-1 overflow-auto text-xs">{JSON.stringify(selectedRow.device || {}, null, 2)}</pre>
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm md:col-span-2">
                  <p className="text-xs uppercase tracking-wide text-slate-500">Metadata</p>
                  <pre className="mt-1 overflow-auto text-xs">{JSON.stringify(selectedRow.metadata || {}, null, 2)}</pre>
                </div>
              </div>
              <div className="mt-4 rounded-xl border border-slate-950 bg-slate-950 p-3">
                <p className="mb-2 text-xs uppercase tracking-wide text-slate-300">Raw JSON</p>
                <pre className="overflow-auto text-xs text-slate-100">{JSON.stringify(selectedRow, null, 2)}</pre>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
}
